import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PackAttachment } from "../Types";

type Props = {
  modality: "image" | "audio" | "text" | "tts";
  value?: string | null;
  setValue: (value: string | null) => void;

  attachments: PackAttachment[];
};

export const ModalityPill = (props: Props) => {
  const { value, modality, attachments, setValue } = props;
  const [showDropdown, setShowDropdown] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [hoverExitTimeout, setHoverExitTimeout] = useState<
    NodeJS.Timeout | null
  >(null);

  const [text, setText] = useState(value);
  const [hasChangedThisEdit, setHasChangedThisEdit] = useState(false);
  const textInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => {
      textInputRef?.current?.focus();
    });
  }, [textInputRef]);

  const open = useCallback((triggerCloseDelay: boolean) => {
    setShowDropdown(true);
    setTimeout(() => {
      textInputRef?.current?.focus();
    });

    setHasChangedThisEdit(false);

    if (triggerCloseDelay) {
      // setHoverExitTimeout(setTimeout(() => {
      //   setShowDropdown(false);
      // }, 6700));
    }
  }, [setShowDropdown, textInputRef, setHasChangedThisEdit]);

  const stayOpen = useCallback(() => {
    if (hoverExitTimeout) {
      clearTimeout(hoverExitTimeout);
      setHoverExitTimeout(null);
    }
  }, [hoverExitTimeout, setHoverExitTimeout]);

  const close = useCallback(() => {
    setShowDropdown(false);
    setHovering(false);
    setHoverExitTimeout(null);
  }, [setShowDropdown, setHovering, setHoverExitTimeout]);

  useEffect(() => {
    const fn = () => {
      if (hovering || !showDropdown) {
        // Skip handling if actively hovered, or not open
        return;
      }
      close();
    };
    document.addEventListener("click", fn);

    return () => document.removeEventListener("click", fn);
  }, [
    hovering,
    showDropdown,
    close,
  ]);

  const filteredAttachments = useMemo(() => {
    if (!text || !hasChangedThisEdit) {
      return attachments;
    }
    const lowercasedText = text.toLowerCase();
    return attachments.filter((x) =>
      x.name.toLowerCase().includes(lowercasedText)
    );
  }, [attachments, text, hasChangedThisEdit]);

  const specialIds = useMemo(() => {
    const specials: ReactNode[] = [];
    const vals = [
      ".mp3",
      ".wav",
      ".ogg",
      ".png",
      ".jpg",
      ".jpeg",
      ".gif",
      ".mp4",
    ];
    for (const val of vals) {
      if (attachments.some((a) => a.name.toLowerCase().endsWith(val))) {
        specials.push(
          <div
            key={`id${val}`}
            className={"dropdown-item"}
            onClick={() => {
              setText(`{{ID}}${val}`);
              setValue(`{{ID}}${val}`);
            }}
          >
            <strong>Special:</strong> {"{{ID}}"}
            {val}
          </div>,
        );
      }
    }
    return specials;
  }, [attachments, setValue, setText]);

  return (
    <div
      className={`pill hoverable selectable dropdown-container ${
        showDropdown ? "open" : "closed"
      } ${(!props.value && !showDropdown) ? "fade" : ""}`}
      onMouseEnter={() => {
        setHovering(true);
        if (hoverExitTimeout) {
          clearTimeout(hoverExitTimeout);
          setHoverExitTimeout(null);
        }
      }}
      onMouseLeave={() => {
        setHovering(false);
        setHoverExitTimeout(setTimeout(() => {
          close();
        }, 1200));
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();

        if (showDropdown) {
          // Close
          close();
        } else {
          // Open
          open(true);
        }
      }}
    >
      <img
        className={"icon"}
        src={`/icons/image-icon.png`}
        alt={`icon`}
      />
      {showDropdown
        ? (
          <input
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              textInputRef.current?.focus();
            }}
            ref={textInputRef}
            type="text"
            value={text ?? ""}
            onChange={(e) => {
              stayOpen();
              setText(e.target.value);
              setValue(e.target.value);
              setHasChangedThisEdit(true);
            }}
          />
        )
        : <span>{value ? value : "Add"}</span>}
      {showDropdown && (
        <div
          className={"dropdown"}
        >
          {specialIds}
          {filteredAttachments.map((a) => {
            return (
              <div
                key={a.id}
                className={"dropdown-item"}
                onClick={() => {
                  setText(a.name);
                  setValue(a.name);
                }}
              >
                <strong>{a.name}</strong>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
